import { RefObject, useEffect } from "react";
import gsap from "gsap";
import { shouldReduceOrizonteMotion } from "@/lib/motion-registry";

type MotionScopeRef<T extends HTMLElement = HTMLElement> = RefObject<T | null>;

export const useGsapStagger = (
  scopeRef: MotionScopeRef,
  selector: string,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        selector,
        { autoAlpha: 0, y: 18, filter: "blur(6px)" },
        {
          autoAlpha: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.65,
          stagger: 0.055,
          ease: "power3.out",
        }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, selector, ...deps]);
};

export const useGsapHoverTilt = (scopeRef: MotionScopeRef, selector: string) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    let cleanups: Array<() => void> = [];
    const ctx = gsap.context(() => {
      const elements = gsap.utils.toArray<HTMLElement>(selector);
      cleanups = elements.map((element) => {
        const glare = element.querySelector<HTMLElement>("[data-motion-glare]");

        const onMove = (event: MouseEvent) => {
          const rect = element.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;
          const rotateY = gsap.utils.mapRange(0, rect.width, -2.2, 2.2, x);
          const rotateX = gsap.utils.mapRange(0, rect.height, 2.2, -2.2, y);

          gsap.to(element, {
            rotateX,
            rotateY,
            scale: 1.008,
            transformPerspective: 900,
            duration: 0.35,
            ease: "power2.out",
          });

          if (glare) {
            gsap.to(glare, {
              x: x - rect.width / 2,
              y: y - rect.height / 2,
              autoAlpha: 1,
              duration: 0.35,
              ease: "power2.out",
            });
          }
        };

        const onLeave = () => {
          gsap.to(element, {
            rotateX: 0,
            rotateY: 0,
            scale: 1,
            duration: 0.65,
            ease: "elastic.out(1, 0.45)",
          });
          if (glare) {
            gsap.to(glare, { autoAlpha: 0, x: 0, y: 0, duration: 0.45 });
          }
        };

        element.addEventListener("mousemove", onMove);
        element.addEventListener("mouseleave", onLeave);

        return () => {
          element.removeEventListener("mousemove", onMove);
          element.removeEventListener("mouseleave", onLeave);
        };
      });

    }, scope);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      ctx.revert();
    };
  }, [scopeRef, selector]);
};

export const useGsapTimelineThread = (
  scopeRef: MotionScopeRef,
  lineSelector: string,
  nodeSelector: string,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        lineSelector,
        { scaleY: 0, transformOrigin: "top center" },
        { scaleY: 1, duration: 1.05, ease: "power3.out" }
      );
      gsap.fromTo(
        nodeSelector,
        { scale: 0.55, autoAlpha: 0, boxShadow: "0 0 0 rgba(204,255,0,0)" },
        {
          scale: 1,
          autoAlpha: 1,
          boxShadow: "0 0 16px rgba(204,255,0,0.26)",
          duration: 0.55,
          stagger: 0.08,
          ease: "back.out(1.8)",
        }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, lineSelector, nodeSelector, ...deps]);
};

export const useGsapChartReveal = (
  scopeRef: MotionScopeRef,
  selector: string,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        selector,
        { autoAlpha: 0, y: 16, clipPath: "inset(0 100% 0 0)" },
        {
          autoAlpha: 1,
          y: 0,
          clipPath: "inset(0 0% 0 0)",
          duration: 0.9,
          stagger: 0.08,
          ease: "power3.out",
        }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, selector, ...deps]);
};

export const useGsapScenarioPulse = (
  scopeRef: MotionScopeRef,
  active: boolean,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.to("[data-scenario-impact]", {
        boxShadow: active
          ? "0 0 36px rgba(204,255,0,0.12), inset 0 1px 0 rgba(255,255,255,0.05)"
          : "0 0 0 rgba(204,255,0,0)",
        borderColor: active ? "rgba(204,255,0,0.38)" : "rgba(26,38,30,1)",
        duration: 0.55,
        ease: "power3.out",
      });

      if (active) {
        gsap.fromTo(
          "[data-scenario-spark]",
          { autoAlpha: 0, x: -36, y: 18, scale: 0.55 },
          {
            autoAlpha: 1,
            x: 0,
            y: 0,
            scale: 1,
            duration: 0.58,
            ease: "power3.out",
          }
        );
      }
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, active, ...deps]);
};

export const useGsapMeridianSignals = (
  scopeRef: MotionScopeRef,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray<HTMLElement>("[data-meridian-signal]");

      gsap.fromTo(
        cards,
        { autoAlpha: 0, y: 24, rotateX: -6, filter: "blur(10px)" },
        {
          autoAlpha: 1,
          y: 0,
          rotateX: 0,
          filter: "blur(0px)",
          transformPerspective: 900,
          duration: 0.75,
          stagger: 0.07,
          ease: "power3.out",
        }
      );

      cards.forEach((card) => {
        const scoreNode = card.querySelector<HTMLElement>("[data-meridian-score]");
        const score = Number(card.dataset.score || 0);
        if (scoreNode) {
          const counter = { value: 0 };
          gsap.fromTo(
            counter,
            { value: 0 },
            {
              value: score,
              duration: 0.95,
              ease: "power2.out",
              onUpdate: () => {
                scoreNode.textContent = `${Math.round(counter.value)}/100`;
              },
            }
          );
        }

        gsap.fromTo(
          card,
          { boxShadow: "0 0 0 rgba(204,255,0,0)" },
          {
            boxShadow: score >= 75
              ? "0 0 26px rgba(248,113,113,0.14)"
              : "0 0 22px rgba(204,255,0,0.10)",
            duration: 0.7,
            yoyo: true,
            repeat: 1,
            ease: "power2.inOut",
          }
        );
      });
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, ...deps]);
};

export const useGsapMeridianConstellation = (
  scopeRef: MotionScopeRef,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.to("[data-meridian-orb]", {
        x: (index) => (index % 2 === 0 ? 10 : -8),
        y: (index) => (index % 3 === 0 ? -12 : 8),
        scale: (index) => 1 + index * 0.03,
        duration: 2.8,
        stagger: 0.14,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.fromTo(
        "[data-meridian-thread]",
        { scaleX: 0, transformOrigin: "left center" },
        { scaleX: 1, duration: 0.9, stagger: 0.06, ease: "power3.out" }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, ...deps]);
};

export const useGsapChatDynamics = (
  scopeRef: MotionScopeRef,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-chat-message]",
        { autoAlpha: 0, y: 14, scale: 0.98, filter: "blur(6px)" },
        { autoAlpha: 1, y: 0, scale: 1, filter: "blur(0px)", duration: 0.42, stagger: 0.035, ease: "power3.out" }
      );

      gsap.fromTo(
        "[data-chat-card]",
        { autoAlpha: 0, y: 10, rotateY: -8 },
        { autoAlpha: 1, y: 0, rotateY: 0, transformPerspective: 700, duration: 0.48, stagger: 0.045, ease: "back.out(1.45)" }
      );

      gsap.fromTo(
        "[data-chat-action]",
        { autoAlpha: 0, y: 8 },
        { autoAlpha: 1, y: 0, duration: 0.35, stagger: 0.04, ease: "power2.out" }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, ...deps]);
};

export const useGsapActionMagnet = (
  scopeRef: MotionScopeRef,
  selector = "[data-gsap-magnet]",
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    let cleanups: Array<() => void> = [];
    const ctx = gsap.context(() => {
      const elements = gsap.utils.toArray<HTMLElement>(selector);
      cleanups = elements.map((element) => {
        const onMove = (event: MouseEvent) => {
          const rect = element.getBoundingClientRect();
          const x = gsap.utils.mapRange(0, rect.width, -5, 5, event.clientX - rect.left);
          const y = gsap.utils.mapRange(0, rect.height, -3, 3, event.clientY - rect.top);
          gsap.to(element, { x, y, scale: 1.035, duration: 0.24, ease: "power2.out" });
        };
        const onLeave = () => {
          gsap.to(element, { x: 0, y: 0, scale: 1, duration: 0.6, ease: "elastic.out(1,0.45)" });
        };
        element.addEventListener("mousemove", onMove);
        element.addEventListener("mouseleave", onLeave);
        return () => {
          element.removeEventListener("mousemove", onMove);
          element.removeEventListener("mouseleave", onLeave);
        };
      });
    }, scope);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      ctx.revert();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, selector, ...deps]);
};

export const useGsapTypingPulse = (
  scopeRef: MotionScopeRef,
  active: boolean
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.to("[data-typing-dot]", {
        y: active ? -5 : 0,
        autoAlpha: active ? 1 : 0.45,
        duration: 0.38,
        stagger: 0.1,
        repeat: active ? -1 : 0,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, scope);

    return () => ctx.revert();
  }, [scopeRef, active]);
};

export const useGsapTogglePulse = (
  scopeRef: MotionScopeRef,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-settings-toggle].is-on",
        { boxShadow: "0 0 0 rgba(204,255,0,0)" },
        {
          boxShadow: "0 0 22px rgba(204,255,0,0.20)",
          duration: 0.42,
          yoyo: true,
          repeat: 1,
          ease: "power2.inOut",
        }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, ...deps]);
};

export const useGsapNotificationStack = (
  scopeRef: MotionScopeRef,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-notification-item]",
        { autoAlpha: 0, x: 22, filter: "blur(8px)" },
        { autoAlpha: 1, x: 0, filter: "blur(0px)", duration: 0.45, stagger: 0.045, ease: "power3.out" }
      );

      gsap.fromTo(
        "[data-notification-unread]",
        { scale: 0.96, boxShadow: "0 0 0 rgba(204,255,0,0)" },
        {
          scale: 1,
          boxShadow: "0 0 24px rgba(204,255,0,0.12)",
          duration: 0.5,
          yoyo: true,
          repeat: 1,
          ease: "power2.inOut",
        }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, ...deps]);
};

export const useGsapSubscriptionRows = (
  scopeRef: MotionScopeRef,
  deps: unknown[] = []
) => {
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope || shouldReduceOrizonteMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-sub-row]",
        { autoAlpha: 0, x: -18, clipPath: "inset(0 100% 0 0)" },
        { autoAlpha: 1, x: 0, clipPath: "inset(0 0% 0 0)", duration: 0.58, stagger: 0.045, ease: "power3.out" }
      );

      gsap.fromTo(
        "[data-sub-status]",
        { scale: 0.7, autoAlpha: 0 },
        { scale: 1, autoAlpha: 1, duration: 0.38, stagger: 0.04, ease: "back.out(2)" }
      );
    }, scope);

    return () => ctx.revert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRef, ...deps]);
};
